import {
  Alert,
  Badge,
  Button,
  Dropdown,
  Icon,
  LayoutProvider,
  type LayoutProviderProps,
  type SideMenuItem,
  Tooltip,
  type UserAction,
} from '@eleo-rh/core-ui'
import { notification, Progress } from 'antd'
import HseSwitcher from 'components/HseSwitcher'
import RoomBookingSwitcher from 'components/RoomBookingSwitcher'
import UserLabel from 'components/UserLabel'
import { API_URL_PROFILE_SWITCH, API_URL_SSO_LOGOUT } from 'constants/api'
import { API_REMINDERS_COUNT } from 'constants/api-v2'
import { PAGES_WITHOUT_SIDEBAR } from 'constants/sidebar'
import { useAuth } from 'context/AuthProvider'
import { useI18n } from 'context/I18nProvider'
import { useProfile } from 'context/ProfileProvider'
import { useQuery } from 'context/QueryProvider'
import { useMercure } from 'hooks/mercure'
import useSWR from 'hooks/swr'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useRouteLoaderData } from 'react-router-dom'
import type { Profile, Settings } from 'shared/types'
import { getItemPermissions, items, type LayoutSiderItem, type LayoutSiderItems } from '../layout/sider/items'
import Notifications from './header/Notifications'

type LayoutMercureEvent =
  | {
      type: 'new_message'
      notificationType?: 'success' | 'info' | 'warning' | 'error'
      label: string
      labelParams?: Record<string, unknown> | unknown[]
    }
  | {
      type: 'reminders_count_update'
      count: number | string
    }
  | {
      type: 'retroactive_notifications'
      notificationType: 'success' | 'info' | 'warning' | 'error'
      key: string
      message: string
      description: string
      percent: number | null
    }

interface message {
  type: 'success' | 'info' | 'warning' | 'error'
  content: string
}

function itemHasPermission(
  item: LayoutSiderItem,
  hasPermission: (permission: string) => boolean,
  profileType: string,
  settings: Settings,
  inheritedPermissions: string[] = [],
) {
  const itemPermissions = item.permissions?.length ? item.permissions : inheritedPermissions
  const allowedByPermission = !itemPermissions.length || itemPermissions.some(permission => hasPermission(permission))

  const allowedByFeature = profileType === 'super-admin' || !item.feature || settings.features?.[item.feature]

  return allowedByPermission && allowedByFeature
}

function stripItemPermissions(item: LayoutSiderItem): SideMenuItem {
  const { permissions: _permissions, feature: _feature, enabled: _enabled, children, ...cleanItem } = item

  if (!children?.length) return cleanItem as SideMenuItem

  return {
    ...cleanItem,
    children: children.map(stripItemPermissions),
  } as SideMenuItem
}

function filterItemChildren(
  item: LayoutSiderItem,
  hasPermission: (permission: string) => boolean,
  profileType: string,
  settings: Settings,
  inheritedPermissions: string[] = [],
): LayoutSiderItem | null {
  const itemPermissions = item.permissions?.length ? item.permissions : inheritedPermissions
  const filteredChildren = item.children
    ?.map(child => filterItemChildren(child, hasPermission, profileType, settings, itemPermissions))
    .filter((child): child is LayoutSiderItem => child !== null)

  if (item.children?.length && !filteredChildren?.length) return null
  if (!itemHasPermission(item, hasPermission, profileType, settings, inheritedPermissions) && !filteredChildren?.length)
    return null

  return {
    ...item,
    ...(filteredChildren ? { children: filteredChildren } : {}),
  } as LayoutSiderItem
}

function normalizeMenuKey(key?: string) {
  return key?.split('?')[0].replace(/\/$/, '')
}

function pathMatchesMenuKey(currentPath: string, menuKey: string) {
  return currentPath === menuKey || currentPath.startsWith(`${menuKey}/`)
}

function findSelectedKey(items: SideMenuItem[], currentPath: string): string | undefined {
  for (const item of items) {
    if (item.type === 'divider' || item.type === 'group') {
      if ('children' in item && item.children?.length) {
        const selectedChildKey = findSelectedKey(item.children as SideMenuItem[], currentPath)
        if (selectedChildKey) return selectedChildKey
      }
      continue
    }

    if ('children' in item && item.children?.length) {
      const selectedChildKey = findSelectedKey(item.children as SideMenuItem[], currentPath)
      if (selectedChildKey) return selectedChildKey
    }

    const normalizedKey = normalizeMenuKey(String(item.key ?? ''))
    if (normalizedKey && pathMatchesMenuKey(currentPath, normalizedKey)) {
      return String(item.key)
    }
  }

  return undefined
}

interface CoreUILayoutProviderProps extends LayoutProviderProps {}

const CoreUILayoutProvider = ({ children, ...props }: CoreUILayoutProviderProps) => {
  const settings = useRouteLoaderData('root') as Settings
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const [message, setMessage] = useState<message>({ type: 'warning', content: '' })
  const [reminders, setReminders] = useState<number | null>(null)
  const { subscribe } = useMercure()
  const { i18n, locale: lang, setLocale, i18name } = useI18n()
  const { logout, refreshToken, user } = useAuth()
  const { profile, profiles, hasPermission } = useProfile()
  const { client } = useQuery()
  const [api, contextHolder] = notification.useNotification()
  const perimeter = user?.perimeter
  const profileType = profile?.type ?? ''

  useEffect(() => {
    if (!user) return

    const channels = ['/users', `/users/${user.id}`, ...(profile?.id ? [`/profiles/${profile.id}`] : [])]

    const topics = ['/api/2.0/messages', '/api/2.0/reminders', '/notifications']

    const messageEventSource = subscribe(channels, topics, ({ data }: { data: LayoutMercureEvent }) => {
      switch (data.type) {
        case 'new_message':
          setMessage({ type: data.notificationType ?? 'warning', content: i18n(data.label, data.labelParams ?? []) })
          break
        case 'reminders_count_update':
          setReminders(Number(data.count))
          break
        case 'retroactive_notifications': {
          const description = (
            <>
              {/* biome-ignore lint/security/noDangerouslySetInnerHtml: notification descriptions are rendered as trusted server-provided rich text. */}
              <div dangerouslySetInnerHTML={{ __html: data.description }}></div>
              {data.percent !== null && <Progress percent={data.percent} />}
            </>
          )

          api[data.notificationType]({
            key: data.key,
            message: data.message,
            description,
            duration: 0,
            placement: 'bottomRight',
          })
          break
        }
        default:
          break
      }
    })

    return () => messageEventSource?.close()
  }, [api, i18n, profile?.id, subscribe, user])

  const withReminders = hasPermission('SESSION_CAN_VIEW_TASK')

  useSWR(withReminders && user ? `${API_REMINDERS_COUNT}?done=false` : null, {
    onSuccess: data => setReminders(Number(data.count)),
  })

  const handleProfileClick = (val: string | number) => {
    client
      .post(API_URL_PROFILE_SWITCH.replace(':id', String(val)))
      .then((response: { token: string }) => {
        refreshToken(response.token)
        window.location.reload()
      })
      .catch((error: unknown) => {
        api.error({
          message: i18n('internal.error'),
          description: error instanceof Error ? error.message : undefined,
        })
      })
  }

  const handleSwitchLocale = (val: string | number) => {
    const locale = String(val)
    window.localStorage.setItem('locale', locale)
    client.updateHeaders()
    setLocale(locale)
    return navigate(`${pathname}${search}`.replace(lang, locale))
  }

  const handleLogout = () => {
    logout()
    if (settings.ssoEnabled) {
      window.location.replace(API_URL_SSO_LOGOUT)
    } else {
      window.location.href = `/${lang}/login`
    }
  }

  const userActions: UserAction[] = [
    {
      Component: user ? <Notifications /> : null,
    },
    {
      Component:
        withReminders && user ? (
          <Badge count={reminders} offset={[-3, 3]}>
            <Button
              singleIcon="alarm-clock"
              variant="secondary"
              onClick={() => navigate(`reminders/${user.id}/dates`)}
            />
          </Badge>
        ) : null,
      separator: true,
    },
    {
      Component: (
        <div className="flex items-center gap-2 min-w-40">
          <Dropdown
            variant="borderless"
            prefix={<Icon name="language" style={{ marginRight: '8px' }} size={'lg'} />}
            value={lang}
            allowClear={false}
            popupMatchSelectWidth={false}
            showSearch={false}
            onChange={handleSwitchLocale}
            options={(settings.locales || []).map(locale => ({
              label: i18n(`locale.${locale}`),
              value: locale,
            }))}
          />
        </div>
      ),
    },
    {
      Component:
        profiles.length > 1 ? (
          <Dropdown
            variant="borderless"
            value={profile?.id}
            allowClear={false}
            popupMatchSelectWidth={false}
            showSearch={false}
            onChange={handleProfileClick}
            options={profiles.map((p: Profile) => ({
              label: i18name(p),
              value: p.id,
            }))}
          />
        ) : (
          <div>{profile ? i18name(profile) : null}</div>
        ),
      separator: true,
    },
    ...(profileType === 'super-admin'
      ? [
          {
            Component: <HseSwitcher />,
          },
          {
            Component: <RoomBookingSwitcher />,
            separator: true,
          },
        ]
      : []),
    ...(user
      ? [
          {
            Component: (
              <UserLabel
                id={user?.id}
                label={`${user?.firstname} ${user?.lastname?.toUpperCase()}`}
                withIcon={false}
                className="font-bold"
              />
            ),
          },
          {
            Component: (
              <Tooltip placement="left" title={i18n('logout')}>
                <Button variant="secondary" singleIcon="arrow-right-from-bracket" onClick={handleLogout} />
              </Tooltip>
            ),
          },
        ]
      : []),
  ]

  const menuItems = useMemo(() => {
    const { coreItems, adminItems } = items({
      tableauURL: settings.tableauURL,
      i18n,
      perimeter,
      settings,
      profileType,
    }) as LayoutSiderItems

    const filteredCoreItems = coreItems
      .filter(item => item.enabled !== false)
      .filter(item => itemHasPermission(item, hasPermission, profileType, settings))
      .map(stripItemPermissions)

    const filteredAdminItems = adminItems
      .filter(item => item.enabled !== false)
      .map(item => filterItemChildren(item, hasPermission, profileType, settings))
      .filter((item): item is LayoutSiderItem => item !== null)
      .map(stripItemPermissions)

    const showAdminMenu = getItemPermissions(adminItems).some(permission => hasPermission(permission))

    return showAdminMenu && filteredAdminItems.length
      ? [
          ...filteredCoreItems,
          { type: 'divider' } as SideMenuItem,
          {
            key: 'menu.admin',
            icon: 'gear',
            label: i18n('menu.admin'),
            children: filteredAdminItems,
          } as SideMenuItem,
        ]
      : filteredCoreItems
  }, [hasPermission, i18n, perimeter, profileType, settings])
  const match = /(\/[a-z]{2})\/([\w]+)/.exec(pathname)
  const showSider = !(match && PAGES_WITHOUT_SIDEBAR.includes(match[2]))
  const currentPath = pathname.replace(/^\/[a-z]{2}\//, '').replace(/\/$/, '')
  const selectedKey = useMemo(() => findSelectedKey(menuItems, currentPath), [currentPath, menuItems])

  return (
    <>
      {contextHolder}
      <LayoutProvider
        showSider={showSider}
        headerProps={{
          pageTitle: message.content && (
            <Alert.Inline
              title={message.content}
              status={message.type}
              closable={{ onClose: () => setMessage({ type: 'warning', content: '' }) }}
            />
          ),
          userActions,
          classNames: {
            actionsWrapper: 'gap-sm',
          },
        }}
        siderProps={{
          menuProps: {
            items: menuItems,
            subMenuIndent: 16,
            selectedKeys: selectedKey ? [selectedKey] : [],
            styles: {
              root: { paddingBottom: '8rem' },
              itemContent: { whiteSpace: 'normal', lineHeight: '20px' },
              subMenu: {
                itemContent: { whiteSpace: 'normal', lineHeight: '20px' },
              },
            },
          },
          width: 272,
          logoLinkProps: {
            href: `/${lang}/catalog/training`,
            title: settings.version,
            'aria-label': i18n('back_to_homepage'),
          },
        }}
        contentStyle={{ padding: 40 }}
        {...props}
      >
        {children}
      </LayoutProvider>
    </>
  )
}

export default CoreUILayoutProvider
